import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const getElapsedMonths = (startDate) => {
  const now = new Date();
  let months =
    (now.getFullYear() - startDate.getFullYear()) * 12 +
    (now.getMonth() - startDate.getMonth());

  if (now.getDate() < startDate.getDate()) {
    months -= 1;
  }

  return Math.max(months, 0);
};

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const menuBtnRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const teamStartDate = new Date(2025, 9, 1); // October 1, 2025
  const teamExperienceMonths = getElapsedMonths(teamStartDate);

  // Track screen size
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
      // Reset scrolled state when switching to mobile
      if (window.innerWidth < 768) {
        setIsScrolled(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track scroll position for shrinking navbar (ONLY on desktop)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show shrink effect when scrolled more than 50px - ONLY on desktop
      if (isDesktop && currentScrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDesktop]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        menuBtnRef.current &&
        !menuBtnRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Close mobile menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mobileMenuOpen]);

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setOpenDropdowns({});
  };

  const handleMenuBtnClick = (e) => {
    e.stopPropagation();
    setMobileMenuOpen((prev) => {
      const next = !prev;
      if (!next) {
        setOpenDropdowns({});
      }
      return next;
    });
  };

  const toggleDropdown = (dropdownId) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [dropdownId]: !prev[dropdownId],
    }));
  };

  const scrollToGetInTouch = () => {
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait a moment for page to load, then scroll
      setTimeout(() => {
        const element = document.getElementById('get-in-touch');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    } else {
      // Already on home page, just scroll
      const element = document.getElementById('get-in-touch');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Mobile Industries with sub-items
  const industries = [
    { name: 'Education & EdTech', items: ['School and coaching websites', 'Learning portal setup', 'Education ERP system setup and support', 'Online test and content delivery', 'Student and parent communication tools', 'Data backup and support', 'Custom solutions as per problem, demand, and project requirements'] },
    { name: 'Healthcare & Clinics', items: ['Full healthcare management system setup', 'Appointment and patient workflow systems', 'Clinic and hospital management tools', 'Role-based access and data protection', 'Billing and reporting automation', 'Maintenance and support services', 'Custom solutions as per problem, demand, and project requirements'] },
    { name: 'Retail & E-commerce', items: ['E-commerce website development', 'Payment gateway integration', 'Inventory and order management', 'Customer engagement dashboards', 'Performance and uptime monitoring', 'Custom solutions as per problem, demand, and project requirements'] },
    { name: 'Manufacturing & Distribution', items: ['Process tracking dashboards', 'Inventory and supply-chain visibility', 'Production reporting automation', 'ERP and workflow integration', 'Cloud backup and continuity', 'Custom solutions as per problem, demand, and project requirements'] },
    { name: 'Professional Services', items: ['Business websites and lead funnels', 'CRM and client management tools', 'Document workflow automation', 'Custom dashboards and reports', 'Ongoing technical support', 'Custom solutions as per problem, demand, and project requirements'] },
    { name: 'Real Estate & Construction', items: ['Project and lead tracking portals', 'Property listing and inquiry systems', 'Site progress reporting', 'Vendor and contract workflows', 'Digital document management', 'Custom solutions as per problem, demand, and project requirements'] },
    { name: 'Public Services & NGOs', items: ['Campaign and donation websites', 'Volunteer and beneficiary management', 'Reporting and impact dashboards', 'Secure data collection workflows', 'Affordable and scalable IT support', 'Custom solutions as per problem, demand, and project requirements'] },
  ];

  return (
    <nav className={`transition-all duration-300 ease-out sticky top-0 z-50 ${
      isScrolled 
        ? 'fixed inset-x-0 top-4 mx-auto w-[90%] max-w-4xl border border-gray-200 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl' 
        : 'w-full border-b border-gray-200 bg-white/90 backdrop-blur-lg shadow-sm'
    }`}>
      <div className={`flex items-center justify-between transition-all duration-300 px-6 ${
        isScrolled ? 'mx-auto w-full' : 'max-w-7xl mx-auto'
      } ${
        isScrolled ? 'h-16' : 'h-20'
      }`}>
        {/* Logo Section */}
        <div className={`flex items-center ${isScrolled ? 'gap-1' : 'gap-3'} flex-shrink-0`}>
          <a href="/" className="flex-shrink-0">
            <img 
              src="./img/logo.png" 
              alt="Garud Stacks Logo" 
              className={`transition-all duration-300 animate-[continuousSlideIn_5s_ease-in-out_infinite] ${
                isScrolled ? 'w-16 h-16' : 'w-16 h-16'
              }`} 
            />
          </a>
          <div className={isScrolled ? 'hidden' : ''}>
            <h2 className="font-bold text-lg uppercase tracking-wide">Garud Stacks</h2>
            <p className={`text-[10px] font-bold text-gray-500 uppercase tracking-widest transition-all duration-300 ${
              isScrolled ? 'opacity-0 h-0 hidden' : 'opacity-100'
            }`}>Private Limited</p>
          </div>
        </div>

        {/* Desktop Menu */}
        <div className={`hidden md:flex items-center transition-all duration-300 ${
          isScrolled ? 'flex-1 justify-center ml-12 gap-5' : 'gap-8'
        }`}>
          <div className={`flex items-center ${
            isScrolled ? 'gap-5' : 'gap-10'
          } text-sm font-bold uppercase tracking-widest text-gray-600`}>
            {/* Home */}
            <a href="/" className="hover:text-black transition-colors hover:text-amber-600 whitespace-nowrap">Home</a>

            {/* Company Dropdown */}
            <div className="relative group">
              <button className="hover:text-black flex items-center gap-1 group-hover:text-amber-600 transition-colors bg-none border-none cursor-pointer p-0">
                Company
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Company Dropdown Content */}
              <div className="absolute left-1/2 -translate-x-1/2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto hover:opacity-100 hover:pointer-events-auto transition-all duration-300 transform group-hover:translate-y-0 -translate-y-2 bg-white shadow-2xl rounded-2xl w-[900px] border border-gray-200 z-50 pt-4">
                <div className="grid grid-cols-[35%_65%] h-[520px]">
                  {/* Left Section */}
                  <div className="bg-gradient-to-br from-amber-50 via-white to-gray-50 p-10 border-r-2 border-amber-200/50 flex flex-col relative overflow-y-auto h-full">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/5 rounded-full blur-3xl"></div>
                    <div className="relative">
                      <div className="inline-block bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-1 rounded-full text-xs font-bold mb-4">Know More</div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-5">About Garud Stacks</h3>
                      <p className="text-sm text-gray-700 leading-relaxed mb-6 font-medium">
                        Garud Stacks is a security-first technology partner helping businesses build scalable cloud infrastructure, custom software, and digital transformation solutions.
                      </p>
                      <div className="space-y-3 mb-8">
                        <div className="flex items-start gap-3">
                          <span className="text-amber-500 font-bold text-lg">→</span>
                          <p className="text-xs text-gray-600 font-semibold">Registered as a private limited company in February 2026</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-amber-500 font-bold text-lg">→</span>
                          <p className="text-xs text-gray-600 font-semibold">Our core team has been delivering projects for {teamExperienceMonths}+ months</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-amber-500 font-bold text-lg">→</span>
                          <p className="text-xs text-gray-600 font-semibold">Focused on practical innovation and measurable outcomes</p>
                        </div>
                      </div>
                    </div>
                    <div className="border-t-2 border-amber-200/30 pt-4 mt-auto">
                      <p className="text-xs text-amber-600 font-bold uppercase tracking-wider">Hover items for details</p>
                    </div>
                  </div>

                  {/* Right Section - About Items */}
                  <div className="p-6 flex flex-col justify-start space-y-2.5 bg-gradient-to-b from-white via-gray-50/50 to-white relative h-full overflow-y-auto">
                    {/* Company Profile */}
                    <Link to="/about" className="group/item cursor-pointer px-5 py-3.5 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 relative border border-transparent hover:border-blue-200 block">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-800 text-sm">Company Profile</span>
                        <svg className="w-5 h-5 text-amber-600 transform group-hover/item:translate-x-2 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </Link>

                    {/* Our Mission */}
                    <div className="group/item cursor-pointer px-5 py-3.5 rounded-xl hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 transition-all duration-300 relative border border-transparent hover:border-green-200">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-800 text-sm">Our Mission</span>
                        <svg className="w-5 h-5 text-amber-600 transform group-hover/item:translate-x-2 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      {/* Mission Tooltip */}
                      <div className="hidden group-hover/item:block hover:block absolute left-1/2 top-full -translate-x-1/2 bg-gradient-to-br from-white to-gray-50 border-2 border-green-200 rounded-xl p-5 w-96 shadow-2xl z-[100] pointer-events-none pt-4">
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-8" style={{borderLeftColor: 'transparent', borderRightColor: 'transparent', borderBottomColor: '#bbf7d0'}}></div>
                        <p className="text-sm font-bold text-green-600 mb-3 uppercase tracking-wider">Mission</p>
                        <p className="text-xs text-gray-700 leading-relaxed font-medium">
                          At Garud Stacks, we empower your business with the wings of AI-driven innovation. Guided by the sharp eye of precision and the straight power of advanced technology, our mission unfolds as we transform your digital journey into a flight of unparalleled success. We don't just provide services; we bring the majestic speed of the Garuda to your vision, ensuring every ascent ends in victory.
                        </p>
                      </div>
                    </div>

                    {/* Core Values */}
                    <div className="group/item cursor-pointer px-5 py-3.5 rounded-xl hover:bg-gradient-to-r hover:from-purple-50 hover:to-violet-50 transition-all duration-300 relative border border-transparent hover:border-purple-200">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-800 text-sm">Core Values</span>
                        <svg className="w-5 h-5 text-amber-600 transform group-hover/item:translate-x-2 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      {/* Core Values Tooltip */}
                      <div className="hidden group-hover/item:block hover:block absolute left-1/2 top-full -translate-x-1/2 bg-gradient-to-br from-white to-gray-50 border-2 border-purple-200 rounded-xl p-5 w-96 shadow-2xl z-[100] pointer-events-none pt-4">
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-8" style={{borderLeftColor: 'transparent', borderRightColor: 'transparent', borderBottomColor: '#e9d5ff'}}></div>
                        <p className="text-sm font-bold text-purple-600 mb-3 uppercase tracking-wider">Core Values</p>
                        <ul className="space-y-2.5 text-xs text-gray-700">
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-purple-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Innovation: Continuously push boundaries</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-purple-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Integrity: Ethical business practices</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-purple-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Excellence: High-quality delivery</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-purple-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">SYNERGISTIC PARTNERSHIP: YOUR SUCCESS IS OUR MISSION</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-purple-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">STRAIGHT TALK: NO LIES, NO HIDDEN TERMS</span></li>
                        </ul>
                      </div>
                    </div>

                    {/* Leadership */}
                    <div className="group/item cursor-pointer px-5 py-3.5 rounded-xl hover:bg-gradient-to-r hover:from-amber-50 hover:to-orange-50 transition-all duration-300 relative border border-transparent hover:border-amber-200">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-800 text-sm">Leadership</span>
                        <svg className="w-5 h-5 text-amber-600 transform group-hover/item:translate-x-2 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      {/* Leadership Tooltip */}
                      <div className="hidden group-hover/item:block hover:block absolute left-1/2 top-full -translate-x-1/2 bg-gradient-to-br from-white to-gray-50 border-2 border-amber-200 rounded-xl p-5 w-96 shadow-2xl z-[100] pointer-events-none pt-4">
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-8" style={{borderLeftColor: 'transparent', borderRightColor: 'transparent', borderBottomColor: '#fde68a'}}></div>
                        <p className="text-sm font-bold text-amber-600 mb-3 uppercase tracking-wider">Founding Team</p>
                        <ul className="space-y-2.5 text-xs text-gray-700">
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-amber-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Team collaboration active for 6+ months</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-amber-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Hands-on delivery in web, cloud, and automation projects</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-amber-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Focused on practical, business-ready solutions</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-amber-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Committed to responsive support and client success</span></li>
                        </ul>
                      </div>
                    </div>

                    {/* Company Credentials */}
                    <div className="group/item cursor-pointer px-5 py-3.5 rounded-xl hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all duration-300 relative border border-transparent hover:border-red-200">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-800 text-sm">Company Credentials</span>
                        <svg className="w-5 h-5 text-amber-600 transform group-hover/item:translate-x-2 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      {/* Company Credentials Tooltip */}
                      <div className="hidden group-hover/item:block hover:block absolute left-1/2 bottom-full -translate-x-1/2 bg-gradient-to-br from-white to-gray-50 border-2 border-red-200 rounded-xl p-5 w-96 shadow-2xl z-[100] pointer-events-none pb-4">
                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-8" style={{borderLeftColor: 'transparent', borderRightColor: 'transparent', borderTopColor: '#fecaca'}}></div>
                        <p className="text-sm font-bold text-red-600 mb-3 uppercase tracking-wider">Company Credentials</p>
                        <ul className="space-y-2.5 text-xs text-gray-700">
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-red-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Registered private limited company (February 2026)</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-red-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Core delivery team active for {teamExperienceMonths}+ months</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-red-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Security-first delivery standards</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-red-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Transparent, process-driven execution</span></li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Solutions Dropdown */}
            <div className="relative group">
              <button className="hover:text-black flex items-center gap-1 group-hover:text-amber-600 transition-colors bg-none border-none cursor-pointer p-0">
                Solutions
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Solutions Dropdown Content */}
              <div className="absolute left-1/2 -translate-x-1/2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto hover:opacity-100 hover:pointer-events-auto transition-all duration-300 transform group-hover:translate-y-0 -translate-y-2 bg-white shadow-2xl rounded-2xl w-[900px] border border-gray-200 z-50 pt-4">
                <div className="grid grid-cols-[35%_65%] h-[520px]">
                  {/* Left Section */}
                  <div className="bg-gradient-to-br from-amber-50 via-white to-gray-50 p-10 border-r-2 border-amber-200/50 flex flex-col relative overflow-y-auto h-full">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/5 rounded-full blur-3xl"></div>
                    <div className="relative">
                      <div className="inline-block bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-1 rounded-full text-xs font-bold mb-4">Our Solutions</div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-5">Practical Solutions for Growing Businesses</h3>
                      <p className="text-sm text-gray-700 leading-relaxed mb-6 font-medium">
                        We build practical, high-impact solutions that help businesses improve operations, security, and digital execution.
                      </p>
                      <div className="space-y-3 mb-8">
                        <div className="flex items-start gap-3">
                          <span className="text-amber-500 font-bold text-lg">→</span>
                          <p className="text-xs text-gray-600 font-semibold">End-to-end implementation support</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-amber-500 font-bold text-lg">→</span>
                          <p className="text-xs text-gray-600 font-semibold">Dedicated support all days from 10 AM to 5 PM(IST)</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-amber-500 font-bold text-lg">→</span>
                          <p className="text-xs text-gray-600 font-semibold">Milestone-based delivery with transparent updates</p>
                        </div>
                      </div>
                    </div>
                    <div className="border-t-2 border-amber-200/30 pt-4 mt-auto">
                      <p className="text-xs text-amber-600 font-bold uppercase tracking-wider">Hover solutions for details</p>
                    </div>
                  </div>

                  {/* Right Section - Services Items */}
                  <div className="p-6 flex flex-col justify-start space-y-2.5 bg-gradient-to-b from-white via-gray-50/50 to-white relative h-full overflow-y-auto">
                    {/* Cloud Infrastructure */}
                    <div className="group/item cursor-pointer px-5 py-3.5 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all duration-300 relative border border-transparent hover:border-blue-200">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-800 text-sm">Cloud Infrastructure</span>
                        <svg className="w-5 h-5 text-amber-600 transform group-hover/item:translate-x-2 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      <div className="hidden group-hover/item:block hover:block absolute left-1/2 top-full -translate-x-1/2 bg-gradient-to-br from-white to-gray-50 border-2 border-blue-200 rounded-xl p-5 w-96 shadow-2xl z-[100] pointer-events-none pt-4">
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-8" style={{borderLeftColor: 'transparent', borderRightColor: 'transparent', borderBottomColor: '#93c5fd'}}></div>
                        <p className="text-sm font-bold text-blue-600 mb-3 uppercase tracking-wider">What We Offer</p>
                        <ul className="space-y-2.5 text-xs text-gray-700">
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-blue-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Cloud setup & migration</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-blue-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Containerized deployments</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-blue-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Infrastructure automation</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-blue-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Backup & recovery planning</span></li>
                        </ul>
                      </div>
                    </div>

                    {/* Cybersecurity Solutions */}
                    <div className="group/item cursor-pointer px-5 py-3.5 rounded-xl hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all duration-300 relative border border-transparent hover:border-red-200">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-800 text-sm">Cybersecurity Solutions</span>
                        <svg className="w-5 h-5 text-amber-600 transform group-hover/item:translate-x-2 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      <div className="hidden group-hover/item:block hover:block absolute left-1/2 top-full -translate-x-1/2 bg-gradient-to-br from-white to-gray-50 border-2 border-red-200 rounded-xl p-5 w-96 shadow-2xl z-[100] pointer-events-none pt-4">
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-8" style={{borderLeftColor: 'transparent', borderRightColor: 'transparent', borderBottomColor: '#fca5a5'}}></div>
                        <p className="text-sm font-bold text-red-600 mb-3 uppercase tracking-wider">Protection Services</p>
                        <ul className="space-y-2.5 text-xs text-gray-700">
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-red-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Security assessment & risk review</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-red-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Compliance-ready security setup</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-red-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Access control & account security</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-red-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Incident response support</span></li>
                        </ul>
                      </div>
                    </div>

                    {/* Enterprise Software */}
                    <div className="group/item cursor-pointer px-5 py-3.5 rounded-xl hover:bg-gradient-to-r hover:from-purple-50 hover:to-violet-50 transition-all duration-300 relative border border-transparent hover:border-purple-200">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-800 text-sm">Enterprise Software</span>
                        <svg className="w-5 h-5 text-amber-600 transform group-hover/item:translate-x-2 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      <div className="hidden group-hover/item:block hover:block absolute left-1/2 top-full -translate-x-1/2 bg-gradient-to-br from-white to-gray-50 border-2 border-purple-200 rounded-xl p-5 w-96 shadow-2xl z-[100] pointer-events-none pt-4">
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-8" style={{borderLeftColor: 'transparent', borderRightColor: 'transparent', borderBottomColor: '#e9d5ff'}}></div>
                        <p className="text-sm font-bold text-purple-600 mb-3 uppercase tracking-wider">Development</p>
                        <ul className="space-y-2.5 text-xs text-gray-700">
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-purple-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Custom web application development</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-purple-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">API integrations & workflow sync</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-purple-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Business process automation</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-purple-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">QA and release support</span></li>
                        </ul>
                      </div>
                    </div>

                    {/* Digital Transformation */}
                    <div className="group/item cursor-pointer px-5 py-3.5 rounded-xl hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 transition-all duration-300 relative border border-transparent hover:border-green-200">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-800 text-sm">Digital Transformation</span>
                        <svg className="w-5 h-5 text-amber-600 transform group-hover/item:translate-x-2 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      <div className="hidden group-hover/item:block hover:block absolute left-1/2 top-full -translate-x-1/2 bg-gradient-to-br from-white to-gray-50 border-2 border-green-200 rounded-xl p-5 w-96 shadow-2xl z-[100] pointer-events-none pt-4">
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-8" style={{borderLeftColor: 'transparent', borderRightColor: 'transparent', borderBottomColor: '#bbf7d0'}}></div>
                        <p className="text-sm font-bold text-green-600 mb-3 uppercase tracking-wider">Innovation</p>
                        <ul className="space-y-2.5 text-xs text-gray-700">
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-green-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Digital strategy & execution roadmap</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-green-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">AI-assisted automation use cases</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-green-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Process optimization initiatives</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-green-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Team enablement & handover support</span></li>
                        </ul>
                      </div>
                    </div>

                    {/* Business Hours Support */}
                    <div className="group/item cursor-pointer px-5 py-3.5 rounded-xl hover:bg-gradient-to-r hover:from-amber-50 hover:to-orange-50 transition-all duration-300 relative border border-transparent hover:border-amber-200">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-800 text-sm">Business Hours Support</span>
                        <svg className="w-5 h-5 text-amber-600 transform group-hover/item:translate-x-2 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      <div className="hidden group-hover/item:block hover:block absolute left-1/2 bottom-full -translate-x-1/2 bg-gradient-to-br from-white to-gray-50 border-2 border-amber-200 rounded-xl p-5 w-96 shadow-2xl z-[100] pointer-events-none pb-4">
                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-8" style={{borderLeftColor: 'transparent', borderRightColor: 'transparent', borderTopColor: '#fde68a'}}></div>
                        <p className="text-sm font-bold text-amber-600 mb-3 uppercase tracking-wider">Support Hours</p>
                        <ul className="space-y-2.5 text-xs text-gray-700">
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-amber-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Support available all days, 10 AM to 5 PM (IST)</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-amber-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Support channels: phone, WhatsApp, and email</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-amber-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Response and updates during business hours</span></li>
                          <li className="flex items-start gap-2 hover:translate-x-1 transition-transform"><span className="text-amber-500 font-bold flex-shrink-0 mt-0.5">→</span><span className="font-medium">Issue tracking and scheduled maintenance support</span></li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Industries Dropdown */}
            <div className="relative group">
              <button className="hover:text-black flex items-center gap-1 group-hover:text-amber-600 transition-colors bg-none border-none cursor-pointer p-0">
                Industries
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Industries Dropdown Content */}
              <div className="absolute left-1/2 -translate-x-1/2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto hover:opacity-100 hover:pointer-events-auto transition-all duration-300 transform group-hover:translate-y-0 -translate-y-2 bg-white shadow-2xl rounded-2xl w-[900px] border border-gray-200 z-50 pt-4">
                <div className="grid grid-cols-[35%_65%] h-[520px]">
                  {/* Left Section */}
                  <div className="bg-gradient-to-br from-amber-50 via-white to-gray-50 p-10 border-r-2 border-amber-200/50 flex flex-col relative overflow-y-auto h-full">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/5 rounded-full blur-3xl"></div>
                    <div className="relative">
                      <div className="inline-block bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-1 rounded-full text-xs font-bold mb-4">IT Services by Industry</div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-5">Industries We Currently Support</h3>
                      <p className="text-sm text-gray-700 leading-relaxed mb-6 font-medium">
                        We currently support businesses across selected industries with practical, scalable IT solutions tailored to their workflows.
                      </p>
                      <div className="space-y-3 mb-8">
                        <div className="flex items-start gap-3">
                          <span className="text-amber-500 font-bold text-lg">→</span>
                          <p className="text-xs text-gray-600 font-semibold">Industry-specific implementation approach</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-amber-500 font-bold text-lg">→</span>
                          <p className="text-xs text-gray-600 font-semibold">Compliance-aware solution design</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-amber-500 font-bold text-lg">→</span>
                          <p className="text-xs text-gray-600 font-semibold">Transparent delivery with measurable outcomes</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-amber-500 font-bold text-lg">→</span>
                          <p className="text-xs text-gray-600 font-semibold">Custom solutions as per problem, demand, and project requirements</p>
                        </div>
                      </div>
                    </div>
                    <div className="border-t-2 border-amber-200/30 pt-4 mt-auto">
                      <p className="text-xs text-amber-600 font-bold uppercase tracking-wider">Hover for details</p>
                    </div>
                  </div>

                  {/* Right Section - Industries */}
                  <div className="p-6 flex flex-col justify-start space-y-2.5 bg-gradient-to-b from-white via-gray-50/50 to-white relative h-full overflow-y-auto">
                    {industries.map((industry, idx) => (
                      <div key={idx} className="group/item cursor-pointer px-5 py-3.5 rounded-xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-blue-50 transition-all duration-300 relative border border-transparent hover:border-indigo-200">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-gray-800 text-sm">{industry.name}</span>
                          <svg className="w-5 h-5 text-amber-600 transform group-hover/item:translate-x-2 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                        {/* Industry Details Tooltip */}
                        <div className={`hidden group-hover/item:block hover:block absolute left-1/2 -translate-x-1/2 bg-gradient-to-br from-white to-gray-50 border-2 border-indigo-200 rounded-xl p-5 w-96 shadow-2xl z-[100] pointer-events-none ${idx > 3 ? 'bottom-full pb-4' : 'top-full pt-4'}`}>
                          <div className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 ${idx > 3 ? 'top-full border-t-8' : 'bottom-full border-b-8'}`} style={{borderLeftColor: 'transparent', borderRightColor: 'transparent', borderTopColor: idx > 3 ? '#c7d2fe' : undefined, borderBottomColor: idx > 3 ? undefined : '#c7d2fe'}}></div>
                          <p className="text-sm font-bold text-indigo-600 mb-3 uppercase tracking-wider">{industry.name}</p>
                          <ul className="space-y-2.5 text-xs text-gray-700">
                            {industry.items.map((item, itemIdx) => (
                              <li key={itemIdx} className="flex items-start gap-2 hover:translate-x-1 transition-transform">
                                <span className="text-indigo-500 font-bold flex-shrink-0 mt-0.5">→</span>
                                <span className="font-medium">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <Link to="/contact" className="hover:text-black transition-colors hover:text-amber-600">Contact</Link>
          </div>

          {/* Buttons */}
          <div className={`flex items-center gap-4 flex-shrink-0 transition-all duration-300 ${
            isScrolled ? 'ml-4' : ''
          }`}>
            <button onClick={scrollToGetInTouch} className="px-6 py-2.5 flex items-center bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm font-bold uppercase tracking-widest rounded-md hover:shadow-lg hover:-translate-y-px transition-all duration-300 cursor-pointer">Talk to an Expert</button>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center">
          <button
            ref={menuBtnRef}
            onClick={handleMenuBtnClick}
            type="button"
            className="inline-flex items-center justify-center p-2.5 rounded-lg text-gray-700 hover:text-black hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500"
          >
            <span className="sr-only">{mobileMenuOpen ? 'Close main menu' : 'Open main menu'}</span>
            <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6l12 12M18 6L6 18" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <>
          <button
            type="button"
            aria-label="Close mobile menu"
            onClick={closeMobileMenu}
            className="md:hidden fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]"
          />
          <div ref={mobileMenuRef} className="md:hidden absolute top-full right-3 mt-2 w-[min(92vw,22rem)] rounded-2xl h-auto bg-white shadow-2xl z-50 border border-gray-200 max-h-[calc(100vh-7rem)] overflow-y-auto">
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Menu</p>
              <button
                type="button"
                onClick={closeMobileMenu}
                className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:text-black hover:bg-gray-100"
                aria-label="Close menu panel"
              >
                <svg className="h-5 w-5" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <Link to="/" onClick={closeMobileMenu} className="block text-base font-bold uppercase tracking-wider py-2 text-gray-700 hover:text-amber-600">
              Home
            </Link>

            {/* Company */}
            <div>
              <button
                type="button"
                onClick={() => toggleDropdown('company')}
                aria-expanded={Boolean(openDropdowns.company)}
                className="w-full flex items-center justify-between text-base font-bold uppercase tracking-wider py-2 text-gray-700 hover:text-amber-600 focus:outline-none bg-none border-none cursor-pointer"
              >
                Company
                <svg className={`w-4 h-4 transition-transform ${openDropdowns.company ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openDropdowns.company && (
                <div className="pl-3 py-2 space-y-3 border-l-2 border-amber-500/90 mt-2">
                  <Link
                    to="/about"
                    onClick={closeMobileMenu}
                    className="group block rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-3 shadow-sm hover:shadow-md hover:-translate-y-px transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-gray-800">Company Profile</p>
                        <p className="text-[11px] text-gray-600 mt-1 leading-relaxed">
                          Explore our complete story, services, mission, and vision.
                        </p>
                      </div>
                      <svg className="w-4 h-4 text-blue-600 mt-0.5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>

                  <div className="rounded-xl border border-green-200 bg-gradient-to-br from-green-50/80 via-white to-emerald-50/60 shadow-sm overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleDropdown('company-mission')}
                      aria-expanded={Boolean(openDropdowns['company-mission'])}
                      className="w-full text-left p-3.5 hover:bg-white/60 flex items-center justify-between bg-none border-none cursor-pointer"
                    >
                      <span className="text-sm font-bold text-gray-800">Our Mission</span>
                      <svg className={`w-4 h-4 text-green-700 transition-transform ${openDropdowns['company-mission'] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openDropdowns['company-mission'] && (
                      <div className="px-3.5 pb-3.5 border-t border-green-200/70 bg-white/70">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          At Garud Stacks, we empower businesses with AI-driven innovation and precision-led execution. We turn digital goals into measurable, business-focused outcomes.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50/80 via-white to-violet-50/60 shadow-sm overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleDropdown('company-values')}
                      aria-expanded={Boolean(openDropdowns['company-values'])}
                      className="w-full text-left p-3.5 hover:bg-white/60 flex items-center justify-between bg-none border-none cursor-pointer"
                    >
                      <span className="text-sm font-bold text-gray-800">Core Values</span>
                      <svg className={`w-4 h-4 text-purple-700 transition-transform ${openDropdowns['company-values'] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openDropdowns['company-values'] && (
                      <div className="px-3.5 pb-3.5 border-t border-purple-200/70 bg-white/70">
                        <ul className="space-y-1.5 text-xs text-gray-700 leading-relaxed">
                          <li className="rounded-md bg-purple-100/70 px-2.5 py-1.5">Innovation: Continuously push boundaries</li>
                          <li className="rounded-md bg-purple-100/70 px-2.5 py-1.5">Integrity: Ethical business practices</li>
                          <li className="rounded-md bg-purple-100/70 px-2.5 py-1.5">Excellence: High-quality delivery</li>
                          <li className="rounded-md bg-purple-100/70 px-2.5 py-1.5">SYNERGISTIC PARTNERSHIP: YOUR SUCCESS IS OUR MISSION</li>
                          <li className="rounded-md bg-purple-100/70 px-2.5 py-1.5">STRAIGHT TALK: NO LIES, NO HIDDEN TERMS</li>
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50/80 via-white to-orange-50/60 shadow-sm overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleDropdown('company-leadership')}
                      aria-expanded={Boolean(openDropdowns['company-leadership'])}
                      className="w-full text-left p-3.5 hover:bg-white/60 flex items-center justify-between bg-none border-none cursor-pointer"
                    >
                      <span className="text-sm font-bold text-gray-800">Leadership</span>
                      <svg className={`w-4 h-4 text-amber-700 transition-transform ${openDropdowns['company-leadership'] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openDropdowns['company-leadership'] && (
                      <div className="px-3.5 pb-3.5 border-t border-amber-200/70 bg-white/70">
                        <ul className="space-y-1.5 text-xs text-gray-700 leading-relaxed">
                          <li>- Core delivery team active for {teamExperienceMonths}+ months</li>
                          <li>- Hands-on execution in web, cloud, and automation projects</li>
                          <li>- Practical, business-ready implementation mindset</li>
                          <li>- Responsive support focused on client success</li>
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl border border-red-200 bg-gradient-to-br from-red-50/80 via-white to-pink-50/60 shadow-sm overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleDropdown('company-credentials')}
                      aria-expanded={Boolean(openDropdowns['company-credentials'])}
                      className="w-full text-left p-3.5 hover:bg-white/60 flex items-center justify-between bg-none border-none cursor-pointer"
                    >
                      <span className="text-sm font-bold text-gray-800">Company Credentials</span>
                      <svg className={`w-4 h-4 text-red-700 transition-transform ${openDropdowns['company-credentials'] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openDropdowns['company-credentials'] && (
                      <div className="px-3.5 pb-3.5 border-t border-red-200/70 bg-white/70">
                        <ul className="space-y-1.5 text-xs text-gray-700 leading-relaxed">
                          <li>- Registered private limited company (February 2026)</li>
                          <li>- Core delivery team active for {teamExperienceMonths}+ months</li>
                          <li>- Security-first delivery standards</li>
                          <li>- Transparent, process-driven execution</li>
                        </ul>
                      </div>
                    )}
                  </div>

                  <Link
                    to="/about"
                    onClick={closeMobileMenu}
                    className="group block w-full text-center text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-orange-600 rounded-lg py-2.5 shadow-sm hover:shadow-md transition-all"
                  >
                    Read Full About Us
                  </Link>
                </div>
              )}
            </div>

            {/* Solutions */}
            <div>
              <button
                type="button"
                onClick={() => toggleDropdown('services')}
                aria-expanded={Boolean(openDropdowns.services)}
                className="w-full flex items-center justify-between text-base font-bold uppercase tracking-wider py-2 text-gray-700 hover:text-amber-600 focus:outline-none bg-none border-none cursor-pointer"
              >
                Solutions
                <svg className={`w-4 h-4 transition-transform ${openDropdowns.services ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openDropdowns.services && (
                <div className="pl-4 py-2 space-y-3 border-l-2 border-amber-500 mt-2">
                  <button type="button" onClick={closeMobileMenu} className="w-full text-left text-sm font-medium text-gray-600 hover:text-amber-600 transition-colors">Cloud Infrastructure</button>
                  <button type="button" onClick={closeMobileMenu} className="w-full text-left text-sm font-medium text-gray-600 hover:text-amber-600 transition-colors">Cybersecurity Solutions</button>
                  <button type="button" onClick={closeMobileMenu} className="w-full text-left text-sm font-medium text-gray-600 hover:text-amber-600 transition-colors">Enterprise Software</button>
                  <button type="button" onClick={closeMobileMenu} className="w-full text-left text-sm font-medium text-gray-600 hover:text-amber-600 transition-colors">Digital Transformation</button>
                  <button type="button" onClick={closeMobileMenu} className="w-full text-left text-sm font-medium text-gray-600 hover:text-amber-600 transition-colors">Business Hours Support</button>
                </div>
              )}
            </div>

            {/* Industries */}
            <div>
              <button
                type="button"
                onClick={() => toggleDropdown('industries')}
                aria-expanded={Boolean(openDropdowns.industries)}
                className="w-full flex items-center justify-between text-base font-bold uppercase tracking-wider py-2 text-gray-700 hover:text-amber-600 focus:outline-none bg-none border-none cursor-pointer"
              >
                Industries
                <svg className={`w-4 h-4 transition-transform ${openDropdowns.industries ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openDropdowns.industries && (
                <div className="pl-4 py-2 space-y-3 border-l-2 border-amber-500 mt-2">
                  {industries.map((industry, idx) => (
                    <div key={idx} className="border border-amber-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleDropdown(`industry-${idx}`)}
                        aria-expanded={Boolean(openDropdowns[`industry-${idx}`])}
                        className="w-full text-left font-medium text-gray-700 p-3 hover:bg-amber-50 flex items-center justify-between text-sm bg-none border-none cursor-pointer"
                      >
                        {industry.name}
                        <svg className={`w-4 h-4 transition-transform ${openDropdowns[`industry-${idx}`] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {openDropdowns[`industry-${idx}`] && (
                        <div className="bg-amber-50 p-3 border-t border-amber-200 max-h-52 overflow-y-auto">
                          <p className="text-xs font-bold text-amber-600 mb-2">Solutions:</p>
                          <ul className="space-y-1 text-xs text-gray-700">
                            {industry.items.map((item, itemIdx) => (
                              <li key={itemIdx}>- {item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Link to="/contact" onClick={closeMobileMenu} className="block text-base font-bold uppercase tracking-wider py-2 text-gray-700 hover:text-amber-600">
              Contact
            </Link>

            <div className="pt-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  closeMobileMenu();
                  scrollToGetInTouch();
                }}
                className="w-full px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm font-bold uppercase tracking-wider rounded-lg hover:shadow-md transition-all"
              >
                Talk to an Expert
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </nav>
  );
};

export default Navbar;
